// Settings → Payment accounts (migration 073; Mitch, 2026-11-03: "If it was paid with a card, which card, on which
// platform"): HHL's own ways to pay, a name and the last 4 digits only, never a full number. Payments, run costs and
// paper POs pick one; a card payment must. Only people who can pay change them (server: assertCanPay).
import { useState } from "react";
import type { PayAccount } from "../../../shared/db.gen.ts";
import { PAY_ACCOUNT_KIND_LABELS, PAY_ACCOUNT_KINDS, type PayAccountKind } from "../../../shared/pay-accounts.ts";
import { options } from "../lib/format.ts";
import { mutate } from "../lib/mutate.ts";
import { setSearch } from "../lib/router.tsx";
import { useRow, useRows } from "../lib/sync.ts";
import { CreateDialog } from "../ui/CreateDialog.tsx";
import { Badge } from "../ui/controls.tsx";
import type { Column } from "../ui/DataTable.tsx";
import { ListPage } from "../ui/ListPage.tsx";
import { type FieldDef, RecordPanel } from "../ui/RecordPanel.tsx";

const KIND_OPTIONS = options(Object.fromEntries(PAY_ACCOUNT_KINDS.map((k) => [k, PAY_ACCOUNT_KIND_LABELS[k]])));

const columns: Column<PayAccount>[] = [
  {
    key: "name",
    header: "Name",
    width: "minmax(200px,1.5fr)",
    cell: (a) => <span className={a.active ? "font-medium" : "text-faint line-through"}>{a.name}</span>,
  },
  { key: "kind", header: "Kind", width: "140px", value: (a) => PAY_ACCOUNT_KIND_LABELS[a.kind as PayAccountKind] },
  { key: "last4", header: "Last 4", width: "90px", cell: (a) => (a.last4 ? <span className="font-mono">••{a.last4}</span> : null) },
  { key: "notes", header: "Notes", width: "minmax(200px,2fr)", cell: (a) => <span className="truncate text-muted">{a.notes}</span> },
];

const fields: FieldDef[] = [
  { key: "name", label: "Name" },
  { key: "kind", label: "Kind", type: "select", options: KIND_OPTIONS },
  { key: "last4", label: "Last 4 digits" },
  { key: "active", label: "In use", type: "bool" },
  { key: "notes", label: "Notes", type: "textarea" },
];

export function PayAccountsPage() {
  const accounts = useRows("pay_account");
  const [creating, setCreating] = useState(false);
  return (
    <>
      <ListPage
        title="Payment accounts"
        header={false}
        rows={accounts}
        columns={columns}
        newLabel="Add account"
        onNew={() => setCreating(true)}
        panel={(id, close) => <PayAccountPanel id={id} close={close} />}
      />
      {creating && (
        <CreateDialog
          title="Add a payment account"
          fields={[
            { key: "name", label: "Name", placeholder: "Amex Business" },
            { key: "kind", label: "Kind", type: "select", options: KIND_OPTIONS },
            { key: "last4", label: "Last 4 digits (never the whole number)", placeholder: "1005" },
          ]}
          initial={{ kind: "card" }}
          onClose={() => setCreating(false)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            const ok = await mutate("pay_account.create", { id, name: v.name, kind: v.kind as PayAccountKind, last4: v.last4 ?? "" });
            if (ok) setSearch({ open: id });
            return ok;
          }}
        />
      )}
    </>
  );
}

function PayAccountPanel({ id, close }: { id: string; close: () => void }) {
  const a = useRow("pay_account", id);
  if (!a) return null;
  return (
    <RecordPanel
      title={a.name}
      subtitle={PAY_ACCOUNT_KIND_LABELS[a.kind as PayAccountKind]}
      badges={!a.active ? <Badge tone="warn">Not in use</Badge> : undefined}
      row={a}
      fields={fields}
      update="pay_account.update"
      entity="pay_account"
      onClose={close}
    >
      <p className="px-2 pt-3 text-[12px] text-muted">
        One of Hand Held Legend's ways to pay. Payments and costs paid outside HHL Ops say which account paid and where (the vendor's website, PayPal,
        Alibaba…). Keep only the last 4 digits here, never a whole card or account number. Turn off In use for a card that's gone; what it paid keeps its name.
      </p>
    </RecordPanel>
  );
}
